import { fromEventPattern, merge, timer } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import { takeUntilDestroy } from '../../../../../common/take-until-destroy.decorator';
import { useSelector2 } from '../../../../store-use-selector';
import { TranscriptCategories } from '../model/transcript-categories.model';
import { TranscriptSummaryState } from '../transcript-summary.state';

const l1Selector =
    '.cmps-coaching-detail-pillar-two-transcript-summary .cmps-scrollbar--content > .cmps-accordion > .cmps-accordion-item';
const l2Selector = '.cmps-coaching-activity-status-wrapper';
const scrollbarSelector =
    '.cmps-coaching-detail-pillar-two-transcript-summary .cmps-scrollbar';

let l1State: {} = {};
let l2State: {} = {};

function beforeLoadMore(): void {
    const el = document.querySelector<HTMLElement>(scrollbarSelector);
    if (el != null) {
        el.style.visibility = 'hidden';
    }
}

function loadLoadMore(): void {
    const level1 = Array.from(document.querySelectorAll(l1Selector));
    level1.forEach((l1) => {
        const l1Index = l1.getAttribute('data-load-more-cat-index');
        if (l1State[l1Index] === 'load-more-element') {
            const loadMoreEl =
                l1.querySelector<HTMLElement>('.load-more-button');
            if (loadMoreEl != null) {
                loadMoreEl.classList.add('load-more-button-show');
            }
        }

        const level2 = Array.from(l1.querySelectorAll<HTMLElement>(l2Selector));
        level2.forEach((l2) => {
            const l2Index = `${l1Index ?? ''}-${
                l2.getAttribute('data-load-more-cat-index') ?? ''
            }`;
            if (l2State[l2Index] === 'load-more-element') {
                l2.classList.add(l2State[l2Index]);
            }
        });
    });
}

function checkLoadMore(): void {
    const scrollCont = document.querySelector(scrollbarSelector);
    const scrollContRect = scrollCont?.getBoundingClientRect();
    if (scrollContRect == null) {
        return;
    }

    const level1 = Array.from(
        document.querySelectorAll(
            `${l1Selector}:not([data-load-more-ignore="true"])`,
        ),
    );
    level1.forEach((l1) => {
        const l1Index = l1.getAttribute('data-load-more-cat-index');

        const level2 = Array.from(l1.querySelectorAll<HTMLElement>(l2Selector));

        if (l1.getAttribute('data-load-more-implicitly-opened') === 'true') {
            l1State[l1Index] = 'load-more-static';

            level2.forEach((l2) => {
                const l2Index = `${l1Index ?? ''}-${
                    l2.getAttribute('data-load-more-cat-index') ?? ''
                }`;

                l2State[l2Index] = 'load-more-static';
            });

            return;
        }

        if (
            l1State[l1Index] === 'load-more-static' ||
            l1State[l1Index] === 'load-more-element'
        ) {
            return;
        }

        let loadMore = false;
        level2.forEach((l2, l2i) => {
            const l2Index = `${l1Index ?? ''}-${
                l2.getAttribute('data-load-more-cat-index') ?? ''
            }`;

            if (
                l2State[l2Index] === 'load-more-static' ||
                l2State[l2Index] === 'load-more-element'
            ) {
                return;
            }

            if (l2i === 0) {
                l2State[l2Index] = 'load-more-static';
                return;
            }

            // if a previous l2 item change loadMore to true, then all others also hidden
            if (loadMore) {
                l2.classList.add('load-more-element');
                l2State[l2Index] = 'load-more-element';
                return;
            }

            const rect = l2.getBoundingClientRect();
            const fullyVisible =
                rect.top >= scrollContRect.top &&
                rect.left >= scrollContRect.left &&
                rect.bottom <= scrollContRect.bottom &&
                rect.right <= scrollContRect.right;
            if (!fullyVisible) {
                l2.classList.add('load-more-element');
                l2State[l2Index] = 'load-more-element';
                loadMore = true;
            } else {
                l2State[l2Index] = 'load-more-static';
            }
        });

        if (!loadMore) {
            l1State[l1Index] = 'load-more-static';
            return;
        }

        l1State[l1Index] = 'load-more-element';
        const loadMoreEl = l1.querySelector<HTMLElement>('.load-more-button');
        if (loadMoreEl != null) {
            loadMoreEl.classList.add('load-more-button-show');
        }
    });
}

function afterLoadMore(): void {
    const el = document.querySelector<HTMLElement>(scrollbarSelector);
    if (el != null) {
        el.style.visibility = 'visible';
    }
}

function clearLoadMore(categoryIndex: number, val: string): void {
    const l1 = document.querySelector(
        `${l1Selector}[data-load-more-cat-index="${categoryIndex}"]`,
    );
    if (l1 == null) {
        return;
    }

    const l1Index = l1.getAttribute('data-load-more-cat-index');
    l1State[l1Index] = val;

    const loadMoreEl = l1.querySelector<HTMLElement>('.load-more-button');
    if (loadMoreEl != null) {
        loadMoreEl.classList.remove('load-more-button-show');
    }

    const level2 = Array.from(l1.querySelectorAll<HTMLElement>(l2Selector));
    level2.forEach((l2) => {
        const l2Index = `${l1Index ?? ''}-${
            l2.getAttribute('data-load-more-cat-index') ?? ''
        }`;
        l2State[l2Index] = val;

        l2.classList.remove('load-more-element');
    });
}

export function didClickLoadMore(categoryIndex: number): void {
    clearLoadMore(categoryIndex, 'load-more-static');
}

export function resetLoadMore(categoryIndex: number): void {
    clearLoadMore(categoryIndex, '');
}

export function bindCheckLoadMore(takeUntilBind: any): void {
    let didInitial = false;

    let io: IntersectionObserver;
    fromEventPattern(
        (handler) => {
            io = new IntersectionObserver(handler);
            io.observe(document.querySelector(scrollbarSelector));
        },
        () => {
            io.disconnect();
        },
    )
        .pipe(
            filter((val) => val?.[0]?.[0]?.isIntersecting),
            tap(() => {
                didInitial = true;
                beforeLoadMore();
                checkLoadMore();
                afterLoadMore();
            }),
            take(1),
            takeUntilDestroy(takeUntilBind),
        )
        .subscribe();

    /** after initial load no state is written about which category items were set to be invisible */
    merge(
        useSelector2<{ reload?: boolean }>(
            (state) =>
                (
                    state?.coachingDetail
                        ?.transcriptSummary as TranscriptSummaryState
                )?.pending,
        ).pipe(
            filter(
                ([prevState, state]) =>
                    prevState?.reload === true && state?.reload !== true,
            ),
            tap(() => {
                l1State = {};
                l2State = {};
            }),
        ),
        useSelector2<TranscriptCategories[]>(
            (state) =>
                (
                    state?.coachingDetail
                        ?.transcriptSummary as TranscriptSummaryState
                )?.filteredTranscriptCategories,
        )
            .pipe
            // filter(([prevState, state]) => {
            //     return (
            //         // opening a category
            //         prevState?.some((e, ei) => e.isOpen !== true && state?.[ei]?.isOpen === true) ||
            //         // opening a transcript
            //         prevState?.some((e, ei) => {
            //             return e.subCategories.some((ee, eei) => {
            //                 return ee.transcripts.some((eee, eeei) => eee.isOpen !== state?.[ei]?.subCategories?.[eei]?.transcripts?.[eeei]?.isOpen);
            //             });
            //         })
            //     );
            // }),
            (),
    )
        .pipe(
            filter(() => didInitial),
            switchMap(() => {
                beforeLoadMore();
                return timer(1);
            }),
            tap(() => {
                loadLoadMore();
                checkLoadMore();
                afterLoadMore();
            }),
            takeUntilDestroy(takeUntilBind),
        )
        .subscribe();
}
